import React, { Fragment } from 'react';
import { Link } from '@reach/router';
import {
  compose,
  lifecycle,
  withState,
  withHandlers,
  withProps,
} from 'recompose';
import { connect } from '../../redux/store';
import { I18n, bundle } from '@kineticdata/react';
import papaparse from 'papaparse';
import downloadjs from 'downloadjs';
import { Modal, ModalBody } from 'reactstrap';
import {
  EmptyMessage,
  ErrorMessage,
  LoadingMessage,
  PaginationBar,
  TimeAgo,
  openConfirm,
} from '@kineticdata/bundle-common';
import { PageTitle } from '../shared/PageTitle';
import {
  actions,
  buildFormConfigurationObject,
} from '../../redux/modules/adminForms';
import { Import } from './Import';

export const getStatusColor = status => {
  switch (status) {
    case 'Inactive':
      return 'danger';
    case 'Delete':
      return 'dark';
    default:
      return 'info';
  }
};

export const FormDetailsComponent = ({
  form,
  openModal,
  submissions,
  submissionsError,
  submissionsFiltering,
  submissionsPaging,
  previousPage,
  nextPage,
  goToPage,
  submissionColumns,
  fetchSubmissionsSort,
  fetchSubmissionsFilter,
  fetchSubmissionsPage,
  deleteSubmission,
  deleting,
  handleFilterChange,
  handleFilterKeyDown,
  handleFilterRemove,
  handleDownload,
  handleFilteredDownload,
  importModal,
  toggleImportModal,
}) => {
  const visibleColumns = submissionColumns.filter(c => c.visible);

  return (
    <div className="page-container">
      <div className="page-panel">
        <PageTitle
          parts={[form.name, `Admin Forms`]}
          breadcrumbs={[{ label: 'Admin Forms', to: '..' }]}
          title={form.name}
          subtitle={form.description}
          actions={[
            {
              icon: 'cog',
              label: 'Form Settings',
              to: `settings`,
            },
          ]}
          meta={[
            {
              value: (
                <span
                  className={`badge badge-${getStatusColor(
                    form.status,
                  )} badge-stylized text-muted`}
                >
                  <I18n>{form.status}</I18n>
                </span>
              ),
            },
            { label: 'Type', value: form.type },
            {
              label: 'Created',
              value: (
                <>
                  <TimeAgo timestamp={form.createdAt} />
                  <span className="font-weight-normal">
                    {' '}
                    <I18n>by</I18n>{' '}
                  </span>
                  <span>{form.createdBy}</span>
                </>
              ),
            },
            {
              label: 'Updated',
              value: (
                <>
                  <TimeAgo timestamp={form.updatedAt} />
                  <span className="font-weight-normal">
                    {' '}
                    <I18n>by</I18n>{' '}
                  </span>
                  <span>{form.updatedBy}</span>
                </>
              ),
            },
          ]}
        />

        <div>
          <section>
            <div className="d-flex mb-3">
              {submissions && (
                <div role="group" className="btn-group">
                  <button
                    onClick={handleDownload}
                    value="export"
                    className="btn btn-primary"
                  >
                    <I18n>Export All Records</I18n>
                  </button>
                  <button
                    onClick={handleFilteredDownload}
                    value="export"
                    className="btn btn-primary"
                    disabled={!submissionsFiltering.isFiltered}
                  >
                    <I18n>Export Filtered Records</I18n>
                  </button>
                  <button
                    onClick={toggleImportModal}
                    value="import"
                    className="btn btn-primary"
                  >
                    <I18n>Import Records</I18n>
                  </button>
                </div>
              )}

              <Link
                to={`submissions/new`}
                className="btn btn-secondary ml-auto"
              >
                <span className="fa fa-plus fa-fw" />
                <I18n>New Record</I18n>
              </Link>
            </div>

            {submissions &&
              !!submissionColumns.find(c => c.filterable) && (
                <div className="input-group mb-3">
                  <div className="input-group-prepend">
                    <span className="input-group-text">
                      <I18n>Filter</I18n>
                    </span>
                  </div>
                  {submissionsFiltering.isFiltered &&
                    submissionsFiltering.query.length > 1 && (
                      <div className="input-group-prepend">
                        <div className="input-group-text input-group-filter-badges">
                          {submissionsFiltering.query
                            .slice(0, -1)
                            .map((v, i) => (
                              <button
                                key={`filter-badge-${i}`}
                                className="btn btn-xs btn-dark"
                                onClick={e => handleFilterRemove(v)}
                                aria-label="Clear Filter"
                                title="Clear Filter"
                              >
                                <span>{v}</span>
                                <span className="fa fa-times" />
                              </button>
                            ))}
                        </div>
                      </div>
                    )}
                  <input
                    type="text"
                    className="form-control"
                    value={
                      submissionsFiltering.isFiltered
                        ? submissionsFiltering.query[
                            submissionsFiltering.query.length - 1
                          ]
                        : ''
                    }
                    onChange={handleFilterChange}
                    onKeyDown={handleFilterKeyDown}
                    placeholder="Enter a filter value. Press TAB after a value to do a multi phrase search."
                  />
                  {submissionsFiltering.isFiltered && (
                    <div className="input-group-append">
                      <button
                        className="btn btn-outline-danger"
                        onClick={e => handleFilterRemove()}
                        aria-label="Clear All Filters"
                        title="Clear All Filters"
                      >
                        <span className="fa fa-times" />
                      </button>
                    </div>
                  )}
                </div>
              )}

            {submissionsError ? (
              <ErrorMessage message={submissionsError.message} />
            ) : !submissions ? (
              <LoadingMessage />
            ) : (
              <Fragment>
                <div className="table-responsive">
                  <table className="table table-sm table-striped table--settings">
                    <thead className="sortable">
                      <tr>
                        {visibleColumns.map(c => {
                          const sortClass =
                            submissionsFiltering.sort ===
                            `${c.type === 'value' ? 'values.' : ''}${c.name}`
                              ? submissionsFiltering.direction === 1
                                ? 'sort-asc'
                                : 'sort-desc'
                              : '';
                          return (
                            <th
                              scope="col"
                              key={`thead-${c.type}-${c.name}`}
                              className={`${sortClass} ${
                                !c.sortable ? 'sort-disabled' : ''
                              }`}
                              onClick={
                                c.sortable
                                  ? e =>
                                      fetchSubmissionsSort(
                                        `${
                                          c.type === 'value' ? 'values.' : ''
                                        }${c.name}`,
                                      )
                                  : () => {}
                              }
                            >
                              <I18n>{c.label}</I18n>
                            </th>
                          );
                        })}
                        <th className="sort-disabled" style={{ width: '1%' }} />
                      </tr>
                    </thead>
                    <tbody>
                      {submissionsPaging.data.map(submission => (
                        <tr key={submission.id}>
                          {visibleColumns.map((c, i) => {
                            const val =
                              c.type === 'value'
                                ? submission['values'][c.name]
                                : submission[c.name];
                            const renderVal =
                              c.dataType === 'string'
                                ? val
                                : c.dataType === 'file' && i !== 0
                                  ? val
                                    ? val.map(file => (
                                        <a
                                          key={`${submission.id}-${file.name}`}
                                          href={`${bundle.contextPath()}${file.link.replace(
                                            /^\/[^/]*/,
                                            '',
                                          )}`}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                        >
                                          {file.name}
                                        </a>
                                      ))
                                    : JSON.stringify(val)
                                  : '';
                            return (
                              <td key={`tbody-${c.type}-${c.name}`}>
                                {i === 0 ? (
                                  <Link to={`submissions/${submission.id}`}>
                                    <I18n>{renderVal}</I18n>
                                  </Link>
                                ) : (
                                  <I18n>{renderVal}</I18n>
                                )}
                              </td>
                            );
                          })}
                          <td>
                            <div className="btn-group">
                              <Link
                                className="btn btn-sm btn-info"
                                title="Edit Submission"
                                to={`submissions/${submission.id}`}
                              >
                                <span className={'fa fa-fw fa-pen'} />
                              </Link>
                              <Link
                                className="btn btn-sm btn-success"
                                title="Clone Submission"
                                to={`submissions/new/${submission.id}`}
                              >
                                <span className={'fa fa-fw fa-copy'} />
                              </Link>
                              <button
                                className="btn btn-sm btn-danger"
                                title="Delete Submission"
                                onClick={() => deleteSubmission(submission.id)}
                                disabled={deleting[submission.id]}
                              >
                                <span
                                  className={`fa fa-fw ${
                                    deleting[submission.id]
                                      ? 'fa-spinner fa-spin'
                                      : 'fa-times'
                                  }`}
                                />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      {submissionsPaging.data.size === 0 && (
                        <tr>
                          <td colSpan={visibleColumns.size + 1}>
                            <EmptyMessage
                              title={
                                submissionsFiltering.isFiltered
                                  ? 'No submissions found matching your filter'
                                  : 'No submissions found'
                              }
                              message={
                                submissionsFiltering.isFiltered
                                  ? 'Try a less specific filter value'
                                  : 'Click New Record to create a submission'
                              }
                            />
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                <PaginationBar
                  className="mb-3"
                  previousPage={
                    submissionsPaging.page > 1 ? previousPage : undefined
                  }
                  nextPage={
                    submissionsPaging.page < submissionsPaging.maxPage
                      ? nextPage
                      : undefined
                  }
                  startIndex={
                    submissionsPaging.data.size > 0
                      ? (submissionsPaging.page - 1) *
                          submissionsPaging.pageSize +
                        1
                      : 0
                  }
                  endIndex={
                    submissionsPaging.data.size > 0
                      ? Math.max(
                          Math.min(
                            submissionsPaging.page * submissionsPaging.pageSize,
                            submissionsPaging.count,
                          ),
                          (submissionsPaging.page - 1) *
                            submissionsPaging.pageSize +
                            1,
                        )
                      : 0
                  }
                  count={submissionsPaging.count}
                  countDone={true}
                  pageSize={submissionsPaging.pageSize}
                  // changePageSize={changePageSize}
                  currentPage={submissionsPaging.page - 1}
                  pageCount={submissionsPaging.maxPage}
                  goToPage={goToPage}
                />
              </Fragment>
            )}
          </section>
        </div>
        <Modal isOpen={importModal} toggle={toggleImportModal} size="lg">
          <div className="modal-header">
            <h4 className="modal-title">
              <button
                onClick={toggleImportModal}
                type="button"
                className="btn btn-link"
              >
                <I18n>Close</I18n>
              </button>
              <span>
                <I18n>Import Records</I18n>
              </span>
            </h4>
          </div>
          <ModalBody className="modal-body--import-export">
            <div style={{ padding: '1.5rem' }}>
              <Import />
            </div>
          </ModalBody>
        </Modal>
      </div>
    </div>
  );
};

const createCSV = (submissions, form) => {
  // Create csv string that will be used for download
  return papaparse.unparse(
    submissions.reduce((acc, submission) => {
      let submissionValues = submission.values;
      /** Because of the parser use the fields currently on the form to build the csv string.
       * This will exclude fields (from the csv) that existed on the form but have been removed.
       */
      form.fields.forEach(field => {
        // If older submissions don't have a new field then add it with a value of null.
        if (submissionValues.hasOwnProperty(field.name)) {
          // Checkbox Array values must be stringifyed to retain their array brackets.
          if (Array.isArray(submissionValues[field.name])) {
            submissionValues[field.name] = JSON.stringify(
              submissionValues[field.name],
            );
          }
        } else {
          submissionValues[field.name] = null;
        }
        return null;
      });
      acc.push({
        'Datastore Record ID': submission.id,
        ...submissionValues,
      });
      return acc;
    }, []),
  );
};

const mapStateToProps = state => ({
  submissions: state.adminForms.submissions,
  submissionsError: state.adminForms.submissionsError,
  submissionsFiltering: state.adminForms.submissionsFiltering,
  submissionsPaging: state.adminForms.submissionsPaging,
  importComplete: state.adminForms.importComplete,
});

const mapDispatchToProps = {
  fetchSubmissionsRequest: actions.fetchSubmissionsRequest,
  fetchSubmissionsSort: actions.fetchSubmissionsSort,
  fetchSubmissionsFilter: actions.fetchSubmissionsFilter,
  fetchSubmissionsPage: actions.fetchSubmissionsPage,
  deleteSubmissionRequest: actions.deleteSubmissionRequest,
};

export const FormDetails = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps,
  ),
  withState('importModal', 'setImportModal', false),
  withState('deleting', 'setDeleting', {}),
  withHandlers({
    toggleImportModal: props => () => {
      if (props.importModal && props.importComplete) {
        props.fetchSubmissionsRequest({
          formSlug: props.form.slug,
        });
      }
      props.setImportModal(v => !v);
    },
    deleteSubmission: props => id =>
      openConfirm({
        title: 'Delete Form',
        body: 'Are you sure you want to delete this submission?',
        actionName: 'Delete',
        ok: () => {
          props.setDeleting({ ...props.deleting, [id]: true });
          props.deleteSubmissionRequest({
            id,
            onSuccess: () =>
              props.fetchSubmissionsRequest({ formSlug: props.form.slug }),
            onFailure: () =>
              props.setDeleting({ ...props.deleting, [id]: false }),
          });
        },
      }),
    handleFilterChange: props => e => {
      if (props.submissionsFiltering.query.length > 0) {
        props.fetchSubmissionsFilter([
          ...props.submissionsFiltering.query.slice(0, -1),
          e.target.value,
        ]);
      } else {
        props.fetchSubmissionsFilter([e.target.value]);
      }
    },
    handleFilterKeyDown: props => e => {
      if (e.keyCode === 9 && e.target.value) {
        props.fetchSubmissionsFilter([...props.submissionsFiltering.query, '']);
        e.preventDefault();
      }
    },
    handleFilterRemove: props => value => {
      props.fetchSubmissionsFilter(
        value ? props.submissionsFiltering.query.filter(q => q !== value) : [],
      );
    },
    handleDownload: props => filtered => {
      const csv = createCSV(props.submissions, props.form);
      downloadjs(csv, `${props.form.name}.csv`, 'text/csv');
    },
    handleFilteredDownload: props => filtered => {
      const csv = createCSV(props.submissionsFiltering.data, props.form);
      downloadjs(csv, `${props.form.name}.csv`, 'text/csv');
    },
    previousPage: props => () =>
      props.fetchSubmissionsPage(props.submissionsPaging.page - 1),
    nextPage: props => () =>
      props.fetchSubmissionsPage(props.submissionsPaging.page + 1),
    goToPage: props => page => () => props.fetchSubmissionsPage(page + 1),
  }),
  withProps(props => ({
    submissionColumns: buildFormConfigurationObject(props.form).columns,
  })),
  lifecycle({
    componentDidMount() {
      if (!this.props.submissions) {
        this.props.fetchSubmissionsRequest({
          formSlug: this.props.form.slug,
        });
      }
    },
  }),
)(FormDetailsComponent);
